import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { makeApi } from '../../lib/api';
import { useAuthStore } from '../auth/authStore';
import { AiAssistButtons } from '../ai/AiAssistButtons';
import { ApprovalsPanel } from './ApprovalsPanel';
import { BlockListEditor } from './BlockListEditor';
import { CommentsPanel } from './CommentsPanel';
import { DiffViewer } from './DiffViewer';
import { PresenceIndicator } from './PresenceIndicator';

/**
 * Phase 3 increment C — minimum-viable Story editor.
 *
 * Loads the selected Story (fetched by UUID, with the
 * `published_content` payload from the show endpoint), renders the
 * content as an editable JSON blob, and provides Save (new version)
 * + Publish (flip the pointer) buttons.
 *
 * **Why a JSON blob, not a schema-driven form yet:** schema-driven
 * field rendering needs the ComponentBlueprint CRUD endpoints +
 * blueprint discovery, which is its own increment. A JSON blob is
 * a deliberately ugly placeholder that's strictly more powerful (an
 * editor can save *anything*), and forces the validation contract
 * to live on the server. Schema-driven UI lands in increment C-2.
 *
 * Phase 3 mandate: the editor must NOT silently accept malformed
 * JSON — surface a clear parse-error to the user rather than save
 * a corrupted draft.
 */
export interface StoryEditorProps {
  /** Story UUID — `null` means "nothing selected", render an empty state. */
  storyUuid: string | null;
  /** Provided by the parent so the topbar's status badge can light up. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Called whenever the editor has a *parseable* content blob — pipes
   * into the PreviewPane to live-render edits. NOT called on parse
   * errors (the preview keeps showing the last good version).
   */
  onContentChange?: (content: Record<string, unknown>) => void;
}

export function StoryEditor({
  storyUuid,
  onDirtyChange,
  onContentChange,
}: StoryEditorProps): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const queryClient = useQueryClient();

  const enabled = token !== null && spaceSlug !== null && storyUuid !== null;

  const storyQuery = useQuery({
    enabled,
    queryKey: ['story', spaceSlug, storyUuid],
    queryFn: async () => {
      if (token === null || spaceSlug === null || storyUuid === null) {
        throw new Error('storyQuery ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.getStory(spaceSlug, storyUuid);
      if (result.status !== 'ok') {
        throw new Error(`getStory failed: ${result.status}`);
      }
      return result.data.data;
    },
  });

  const [draftJson, setDraftJson] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState<boolean>(false);
  // Default to structured view so editors who never want to see JSON
  // get the no-code experience by default. JSON view is the escape
  // hatch for shapes the form can't express.
  const [editorMode, setEditorMode] = useState<'structured' | 'json'>('structured');

  // Phase 10 — variant selection. `null` = the Story's default content
  // (preserves the pre-Phase-10 edit flow). A string key = the segment
  // we're currently editing a variant for.
  const [activeVariantKey, setActiveVariantKey] = useState<string | null>(null);

  // List of non-archived segments for the switcher. Cached on the
  // space slug; rarely changes during an edit session.
  const segmentsQuery = useQuery({
    enabled: token !== null && spaceSlug !== null,
    queryKey: ['audience-segments', spaceSlug],
    queryFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('segmentsQuery ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.listSegments(spaceSlug);
      if (result.status !== 'ok') {
        throw new Error(`listSegments failed: ${result.status}`);
      }
      return result.data;
    },
    staleTime: 60_000,
  });

  // Variants for the currently-open Story. Re-fetched when the story
  // changes; kept in sync via invalidation after upsert.
  const variantsQuery = useQuery({
    enabled,
    queryKey: ['story-variants', spaceSlug, storyUuid],
    queryFn: async () => {
      if (token === null || spaceSlug === null || storyUuid === null) {
        throw new Error('variantsQuery ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.listVariants(spaceSlug, storyUuid);
      if (result.status !== 'ok') {
        throw new Error(`listVariants failed: ${result.status}`);
      }
      return result.data;
    },
  });

  const activeSegments = useMemo(
    () =>
      (segmentsQuery.data?.data ?? []).filter((s) => s.archived_at === null),
    [segmentsQuery.data],
  );

  const activeVariant = useMemo(() => {
    if (activeVariantKey === null) return null;
    const found = (variantsQuery.data?.data ?? []).find(
      (v) => v.segment_key === activeVariantKey,
    );
    return found ?? null;
  }, [activeVariantKey, variantsQuery.data]);

  // Computed once per render — MUST live above the early-returns below
  // or React's rules-of-hooks fires error #310 (rendered more hooks
  // than during the previous render) the first time the query
  // resolves and the body switches from "Loading..." to the editor.
  // (Was discovered during the Storyblok-parity audit 2026-05-21
  // when entering the StoryEditor crashed the whole admin tree.)
  const activeSegmentLabel = useMemo(() => {
    if (activeVariantKey === null) return null;
    const match = activeSegments.find((s) => s.key === activeVariantKey);
    return match?.label ?? activeVariantKey;
  }, [activeVariantKey, activeSegments]);

  // When the user switches stories, reset the variant selection so
  // the editor doesn't carry a stale variant into the next story
  // (which may not have a variant for that segment).
  useEffect(() => {
    setActiveVariantKey(null);
  }, [storyUuid]);

  // When the story loads or the active variant changes, prime the
  // draft with the appropriate content:
  //   - default mode (`activeVariantKey === null`): the story's
  //     published_content (same as pre-Phase-10).
  //   - variant mode with an existing variant: that variant's content.
  //   - variant mode without a variant yet: leave the draft empty/
  //     placeholder so the UI can show "Override erstellen" — the
  //     priming below skips writing in that case.
  // The check guards against React's double-render-in-strict-mode
  // resetting an edit-in-progress.
  useEffect(() => {
    if (storyQuery.data === undefined) return;

    if (activeVariantKey !== null) {
      if (activeVariant !== null) {
        const content = activeVariant.content ?? {};
        setDraftJson(JSON.stringify(content, null, 2));
        setParseError(null);
        onDirtyChange?.(false);
        if (content !== null && typeof content === 'object') {
          onContentChange?.(content as Record<string, unknown>);
        }
      }
      // No variant row yet → don't repaint the draft; the variant-
      // empty banner below renders the "Override erstellen" CTA.
      return;
    }

    // Prefer the latest draft (whatever the user was last editing).
    // Fall back to the currently-published content. Storyblok-parity:
    // re-opening a Story should always pick up where you left off,
    // not jump back to the last publish.
    const content =
      storyQuery.data.draft_content
      ?? storyQuery.data.published_content
      ?? {};
    const initial = JSON.stringify(content, null, 2);
    setDraftJson(initial);
    setParseError(null);
    onDirtyChange?.(false);
    if (content !== null && typeof content === 'object') {
      onContentChange?.(content as Record<string, unknown>);
    }
  }, [
    storyQuery.data,
    activeVariantKey,
    activeVariant,
    onDirtyChange,
    onContentChange,
  ]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { content: unknown; message?: string }) => {
      if (token === null || spaceSlug === null || storyUuid === null) {
        throw new Error('save ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.saveVersion(spaceSlug, storyUuid, {
        content: payload.content as Record<string, unknown>,
        message: payload.message,
      });
      if (result.status !== 'ok') {
        throw new Error(`saveVersion failed: ${result.status}`);
      }
      return result.data.data;
    },
    onSuccess: () => {
      // Trigger a re-fetch — both for the story itself and for the
      // sidebar tree (status / updated_at flips).
      queryClient.invalidateQueries({ queryKey: ['story', spaceSlug, storyUuid] });
      queryClient.invalidateQueries({ queryKey: ['stories', spaceSlug] });
      onDirtyChange?.(false);
    },
  });

  // Phase 10 — variant save uses a separate endpoint (PUT
  // /v1/spaces/{slug}/stories/{uuid}/variants/{segment_key}). We
  // keep it in its own mutation so its loading/error UI is distinct
  // from the default save, and so invalidation only hits the variant
  // query (the default story's published_content is unchanged).
  const variantSaveMutation = useMutation({
    mutationFn: async (payload: { segmentKey: string; content: Record<string, unknown> }) => {
      if (token === null || spaceSlug === null || storyUuid === null) {
        throw new Error('variant save ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.upsertVariant(
        spaceSlug,
        storyUuid,
        payload.segmentKey,
        { content: payload.content },
      );
      if (result.status !== 'ok') {
        throw new Error(`upsertVariant failed: ${result.status}`);
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['story-variants', spaceSlug, storyUuid],
      });
      onDirtyChange?.(false);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (token === null || spaceSlug === null || storyUuid === null) {
        throw new Error('publish ran without prerequisites');
      }
      const api = makeApi(token);
      const result = await api.publish(spaceSlug, storyUuid);
      if (result.status !== 'ok') {
        throw new Error(`publish failed: ${result.status}`);
      }
      return result.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['story', spaceSlug, storyUuid] });
      queryClient.invalidateQueries({ queryKey: ['stories', spaceSlug] });
    },
  });

  function handleSave(): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draftJson);
    } catch (err) {
      setParseError(
        err instanceof Error ? err.message : 'JSON parse failed',
      );
      return;
    }
    setParseError(null);

    if (activeVariantKey !== null) {
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setParseError('Variant content must be a JSON object.');
        return;
      }
      variantSaveMutation.mutate({
        segmentKey: activeVariantKey,
        content: parsed as Record<string, unknown>,
      });
      return;
    }
    saveMutation.mutate({ content: parsed });
  }

  /**
   * "Override erstellen" — seeds the variant draft with the current
   * default content so the editor doesn't start from an empty blob.
   * This stays client-side until the user hits Save; the variant
   * row is only created on the first successful PUT.
   */
  function handleCreateOverride(): void {
    if (storyQuery.data === undefined) return;
    // Prefer the latest draft (whatever the user was last editing).
    // Fall back to the currently-published content. Storyblok-parity:
    // re-opening a Story should always pick up where you left off,
    // not jump back to the last publish.
    const content =
      storyQuery.data.draft_content
      ?? storyQuery.data.published_content
      ?? {};
    setDraftJson(JSON.stringify(content, null, 2));
    setParseError(null);
    onDirtyChange?.(true);
    if (content !== null && typeof content === 'object') {
      onContentChange?.(content as Record<string, unknown>);
    }
  }

  if (storyUuid === null) {
    return (
      <p className="text-sm text-zinc-500">
        Wähle eine Story aus dem Baum, um mit dem Bearbeiten zu beginnen.
      </p>
    );
  }

  if (storyQuery.isPending) {
    return <p className="text-sm text-zinc-500">Lade Story…</p>;
  }

  if (storyQuery.isError) {
    return (
      <p
        role="alert"
        className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
      >
        {(storyQuery.error as Error).message}
      </p>
    );
  }

  const story = storyQuery.data;

  // Phase 10 — variant mode flag. When a non-default segment is
  // selected AND no variant row exists yet, we render the "Override
  // erstellen" CTA in place of the editor body. Once the user clicks
  // create-override (or once they save), the editor shows normally.
  const inVariantMode = activeVariantKey !== null;
  const needsOverridePrompt =
    inVariantMode &&
    activeVariant === null &&
    // Only show the prompt once the variants list has loaded; until
    // then we don't know if there's an existing variant to load.
    !variantsQuery.isPending &&
    // The user hasn't seeded the override yet — detect via a dirty
    // draft that doesn't match the (empty) variant state.
    draftJson.trim().length === 0;

  return (
    <div className="flex h-full flex-col gap-4">
      <header>
        <h1 className="text-lg font-semibold tracking-tight text-zinc-900">
          {story?.full_path}
        </h1>
        <p className="mt-1 text-xs text-zinc-500">
          <span className="font-mono">
            {story?.lang} · {story?.status}
          </span>
          {story?.published_version_id === null ? (
            <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-amber-900">
              No published version yet
            </span>
          ) : null}
          {inVariantMode ? (
            <span className="ml-2 rounded bg-teal-100 px-2 py-0.5 font-medium text-teal-900">
              Variant · {activeSegmentLabel}
            </span>
          ) : null}
        </p>
      </header>

      {activeSegments.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-1 rounded-md border border-zinc-200 bg-zinc-50 p-1 text-xs"
          role="group"
          aria-label="Varianten-Wechsler"
          data-testid="variant-switcher"
        >
          <span className="px-2 text-[11px] uppercase tracking-wider text-zinc-500">
            Variant:
          </span>
          <button
            type="button"
            onClick={() => setActiveVariantKey(null)}
            data-testid="variant-default"
            className={
              'rounded px-2 py-1 transition ' +
              (activeVariantKey === null
                ? 'bg-white font-medium text-zinc-900 shadow-sm'
                : 'text-zinc-600 hover:text-zinc-900')
            }
          >
            Default
          </button>
          {activeSegments.map((segment) => {
            const isActive = activeVariantKey === segment.key;
            const hasVariant = (variantsQuery.data?.data ?? []).some(
              (v) => v.segment_key === segment.key,
            );
            return (
              <button
                key={segment.key}
                type="button"
                onClick={() => setActiveVariantKey(segment.key)}
                data-testid={`variant-${segment.key}`}
                className={
                  'rounded px-2 py-1 transition ' +
                  (isActive
                    ? 'bg-white font-medium text-zinc-900 shadow-sm'
                    : 'text-zinc-600 hover:text-zinc-900')
                }
                title={segment.label}
              >
                {segment.label}
                {hasVariant ? (
                  <span
                    aria-hidden
                    className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-teal-500"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {needsOverridePrompt ? (
        <div
          className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-700"
          data-testid="variant-override-prompt"
        >
          <p className="mb-3">
            Für <strong>{activeSegmentLabel}</strong> existiert noch keine
            Variante. Standard-Inhalt als Ausgangspunkt übernehmen?
          </p>
          <button
            type="button"
            onClick={handleCreateOverride}
            data-testid="variant-create-override"
            className="rounded bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800"
          >
            Override für {activeSegmentLabel} erstellen
          </button>
        </div>
      ) : null}

      {storyUuid !== null && !needsOverridePrompt ? (
        <AiAssistButtons
          storyUuid={storyUuid}
          currentJson={draftJson}
          onResult={(next) => {
            setDraftJson(next);
            onDirtyChange?.(true);
            try {
              const parsed = JSON.parse(next);
              if (parsed !== null && typeof parsed === 'object') {
                onContentChange?.(parsed as Record<string, unknown>);
              }
            } catch {
              // ignore parse — preview stays stale
            }
          }}
        />
      ) : null}

      {/* Phase 14 — approval pipeline panel (renders only when the Space
          has at least one approval-gated stage). */}
      {storyUuid !== null && !needsOverridePrompt ? (
        <ApprovalsPanel storyUuid={storyUuid} />
      ) : null}

      {/* Phase 15 — Real-time presence chips + comments panel. Presence
          auto-hides when nobody else is on the Story; comments panel
          renders always so editors can leave notes for themselves. */}
      {storyUuid !== null && !needsOverridePrompt ? (
        <PresenceIndicator storyUuid={storyUuid} />
      ) : null}
      {storyUuid !== null && !needsOverridePrompt ? (
        <CommentsPanel storyUuid={storyUuid} />
      ) : null}

      {needsOverridePrompt ? null : (
      <div className="flex items-center gap-1 rounded-md bg-zinc-100 p-1 text-xs" role="tablist" aria-label="Editor-Modus">
        {(['structured', 'json'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={editorMode === m}
            onClick={() => setEditorMode(m)}
            className={
              'flex-1 rounded px-2 py-1 capitalize transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 ' +
              (editorMode === m
                ? 'bg-white font-medium text-zinc-900 shadow-sm'
                : 'text-zinc-600 hover:text-zinc-900')
            }
            data-testid={`editor-mode-${m}`}
          >
            {m === 'structured' ? 'Form' : 'JSON'}
          </button>
        ))}
      </div>
      )}

      {needsOverridePrompt ? null : editorMode === 'structured' ? (
        (() => {
          // Try to parse the current draft for the structured editor.
          // If it doesn't parse, fall back to JSON view automatically —
          // editors shouldn't see a "no UI rendered" black hole. The
          // parseError already explains why.
          let parsed: Record<string, unknown> | null = null;
          try {
            const v = JSON.parse(draftJson);
            if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
              parsed = v as Record<string, unknown>;
            }
          } catch {
            // fall through
          }
          if (parsed === null) {
            return (
              <p
                role="alert"
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              >
                Structured view needs a valid JSON object — switch to JSON view to fix the shape.
              </p>
            );
          }
          return (
            <BlockListEditor
              value={parsed}
              onChange={(next) => {
                const nextJson = JSON.stringify(next, null, 2);
                setDraftJson(nextJson);
                setParseError(null);
                onDirtyChange?.(true);
                onContentChange?.(next);
              }}
            />
          );
        })()
      ) : (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
            Content (JSON)
          </span>
          <textarea
            value={draftJson}
            onChange={(e) => {
              const next = e.target.value;
              setDraftJson(next);
              setParseError(null);
              onDirtyChange?.(true);
              // Live preview push: try to parse on every keystroke. If
              // the JSON is currently malformed (mid-edit), we silently
              // skip the push and keep the preview showing the last
              // good version. A clean editing pause "settles" the
              // preview without a debounce or explicit save.
              try {
                const parsed = JSON.parse(next);
                if (parsed !== null && typeof parsed === 'object') {
                  onContentChange?.(parsed as Record<string, unknown>);
                }
              } catch {
                // Mid-edit; preview stays stale until JSON re-balances.
              }
            }}
            spellCheck={false}
            className="min-h-[260px] flex-1 rounded-md border border-zinc-300 px-3 py-2 font-mono text-xs leading-relaxed focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900"
            data-testid="story-content-textarea"
          />
        </label>
      )}

      {parseError !== null ? (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
        >
          JSON parse error: {parseError}
        </p>
      ) : null}

      {saveMutation.isError ? (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
        >
          {(saveMutation.error as Error).message}
        </p>
      ) : null}

      {variantSaveMutation.isError ? (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
        >
          {(variantSaveMutation.error as Error).message}
        </p>
      ) : null}

      {publishMutation.isError ? (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
        >
          {(publishMutation.error as Error).message}
        </p>
      ) : null}

      {needsOverridePrompt ? null : (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={
            (inVariantMode ? variantSaveMutation.isPending : saveMutation.isPending) ||
            parseError !== null
          }
          data-testid={inVariantMode ? 'variant-save-button' : 'story-save-button'}
          className="inline-flex min-h-11 items-center rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {inVariantMode
            ? variantSaveMutation.isPending
              ? 'Speichere Variante…'
              : `Variante speichern (${activeSegmentLabel})`
            : saveMutation.isPending
              ? 'Speichere…'
              : 'Als neue Version speichern'}
        </button>
        {inVariantMode ? null : (
          <button
            type="button"
            onClick={() => setShowDiff(true)}
            disabled={storyUuid === null}
            className="inline-flex min-h-11 items-center rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Versionen vergleichen
          </button>
        )}
        {inVariantMode ? null : (
          <button
            type="button"
            onClick={() => publishMutation.mutate()}
            disabled={publishMutation.isPending}
            className="inline-flex min-h-11 items-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {publishMutation.isPending ? 'Veröffentliche…' : 'Aktuellste Version veröffentlichen'}
          </button>
        )}

        {!inVariantMode && saveMutation.data !== undefined ? (
          <span className="text-xs text-zinc-500">
            Saved as v{saveMutation.data.version.version_no}
          </span>
        ) : null}
        {inVariantMode && variantSaveMutation.data !== undefined ? (
          <span className="text-xs text-green-700">
            Variante gespeichert · {variantSaveMutation.data.segment_label}
          </span>
        ) : null}
        {!inVariantMode && publishMutation.data !== undefined ? (
          <span className="text-xs text-green-700">
            Published v{publishMutation.data.published_version.version_no}
          </span>
        ) : null}
      </div>
      )}

      {showDiff && storyUuid !== null ? (
        <DiffViewer storyUuid={storyUuid} onClose={() => setShowDiff(false)} />
      ) : null}
    </div>
  );
}
